
import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';

// Layout Components
import DoctorLayout from './ui/doctor-area/layout';
import PatientPageLayout from './components/layouts/PatientPageLayout';

// Auth Components
import { AuthProvider } from './contexts/AuthContext';
import ProtectedRoute from './components/auth/ProtectedRoute';
import EmailVerificationCheck from './components/auth/EmailVerificationCheck';

// Doctor Pages
import DoctorDashboard from './ui/doctor-area/DoctorDashboard';
import DoctorProfile from './ui/doctor-area/DoctorProfile';
import DoctorSettings from './ui/doctor-area/DoctorSettings';
import DoctorAdsPage from './ui/doctor-area/DoctorAdsPage';
import DoctorStatisticsPage from './ui/doctor-area/DoctorStatisticsPage';
import MyPatientsPage from './ui/doctor-area/MyPatientsPage';
import DoctorChatPage from './pages/DoctorChatPage';
import NotificationCenter from './ui/doctor-area/NotificationCenter';
import DoctorServicesPage from './ui/doctor-area/DoctorServicesPage';
import DoctorAvailability from './ui/doctor-area/DoctorAvailability';
import CaseFilePage from './ui/doctor-area/CaseFilePage';
import CaseFileOverview from './ui/doctor-area/CaseFileOverview';
import PostTreatmentReportPage from './ui/doctor-area/post-treatment/PostTreatmentReportPage';
import DoctorBeforeAfterGallery from './ui/doctor-area/before-after/DoctorBeforeAfterGallery';
import DoctorStoriesPage from './ui/doctor-area/stories/DoctorStoriesPage';
import DoctorReviewsPage from './ui/doctor-area/reviews/DoctorReviewsPage';
import DoctorAppointmentsPage from './ui/doctor-area/appointments/DoctorAppointmentsPage';
import CreateTreatmentPlan from './pages/CreateTreatmentPlan';
import PatientSessionsManager from './ui/doctor-area/patient-sessions/PatientSessionsManager';
import MyTreatmentPlansPage from './ui/doctor-area/treatment-plans/MyTreatmentPlansPage';
import IncomeSummaryPage from './ui/doctor-area/finance/IncomeSummaryPage';
import FinancePage from './ui/doctor-area/finance/FinancePage';
import FollowUpReportsPage from './ui/doctor-area/follow-up/FollowUpReportsPage';
import TreatmentSessionsPage from './ui/doctor-area/treatment-sessions/TreatmentSessionsPage';
import CalendarViewPage from './ui/doctor-area/calendar/CalendarViewPage';
import MedicalDocumentsPage from './ui/doctor-area/medical-documents/MedicalDocumentsPage';
import FAQSupportPage from './ui/doctor-area/FAQSupportPage';
import AIAssistantPage from './ui/doctor-area/support/AIAssistantPage';
import CreateFollowUpReportPage from './ui/doctor-area/follow-up/CreateFollowUpReportPage';
import ProfessionalDocumentsPage from './ui/doctor-area/documents/ProfessionalDocumentsPage';
import ContactSupportPage from './ui/doctor-area/support/ContactSupportPage';

// Patient Pages
import PatientChatPage from './pages/PatientChatPage';
import Notifications from './ui/patient-area/notifications/Notifications';

// Loading and Error Pages
import ComingSoonPage from './components/shared/ComingSoonPage';

function App() {
  // Redirect /create-treatment-plan/new to treatment plans page
  const RedirectToTreatmentPlans = () => {
    return <Navigate to="/treatment-plans" replace />;
  };

  return (
    <AuthProvider>
      <Routes>
        <Route path="/" element={<DoctorLayout />}>
          {/* Dashboard */}
          <Route path="dashboard" element={<DoctorDashboard />} />
          <Route index element={<Navigate to="/dashboard" replace />} />
          
          {/* Profile & Documents */}
          <Route path="profile/:profileId?" element={<DoctorProfile />} />
          <Route path="settings" element={<DoctorSettings />} />
          <Route path="documents" element={<ProfessionalDocumentsPage />} />
          <Route path="services" element={<DoctorServicesPage />} />
          
          {/* Patients & Treatments */}
          <Route path="patients" element={<MyPatientsPage />} />
          <Route path="case-file/:patientId" element={<CaseFilePage />} />
          <Route path="case-file" element={<CaseFilePage />} />
          <Route path="case-file/overview" element={<CaseFileOverview />} />
          <Route path="patient-sessions/:patientId" element={<PatientSessionsManager />} />
          <Route path="treatment-plans" element={<MyTreatmentPlansPage />} />
          <Route path="treatment-sessions" element={<TreatmentSessionsPage />} />
          <Route path="follow-up-reports" element={<FollowUpReportsPage />} />
          <Route path="follow-up-reports/:patientId/create" element={<CreateFollowUpReportPage />} />
          <Route path="post-treatment" element={<PostTreatmentReportPage />} />
          <Route path="post-treatment/:patientId" element={<PostTreatmentReportPage />} />
          <Route path="post-treatment/:patientId/view" element={<PostTreatmentReportPage />} />
          <Route path="medical-documents" element={<MedicalDocumentsPage />} />
          
          {/* Appointments & Scheduling */}
          <Route path="appointments" element={<DoctorAppointmentsPage />} />
          <Route path="availability" element={<DoctorAvailability />} />
          <Route path="calendar-view" element={<CalendarViewPage />} />
          <Route path="calendar" element={<CalendarViewPage />} />
          <Route path="reschedule" element={<ComingSoonPage title="Reschedule/Cancel Appointments" 
            description="This feature will allow you to manage appointment rescheduling and cancellations." />} />
          
          {/* Performance & Marketing */}
          <Route path="statistics" element={<DoctorStatisticsPage />} />
          <Route path="ads" element={<DoctorAdsPage />} />
          <Route path="finance" element={<FinancePage />} />
          <Route path="income" element={<IncomeSummaryPage />} />
          <Route path="reviews" element={<DoctorReviewsPage />} />
          <Route path="stories" element={<DoctorStoriesPage />} />
          <Route path="before-after" element={<DoctorBeforeAfterGallery />} />
          <Route path="earnings-reports" element={<ComingSoonPage title="Earnings Reports" 
            description="Detailed reports about your earnings will be available here." />} />
          <Route path="invoices" element={<ComingSoonPage title="Invoices" 
            description="All your invoices will be accessible from this page." />} />
          <Route path="withdrawal-requests" element={<ComingSoonPage title="Withdrawal Requests" 
            description="Request withdrawals and track your withdrawal history." />} />
          
          {/* Communication */}
          <Route path="messages" element={<DoctorChatPage />} />
          <Route path="messages/:patientId" element={<DoctorChatPage />} />
          <Route path="notifications" element={<NotificationCenter />} />
          
          {/* Support */}
          <Route path="faq-support" element={<FAQSupportPage />} />
          <Route path="ai-assistant" element={<AIAssistantPage />} />
          <Route path="contact-support" element={<ContactSupportPage />} />
          <Route path="privacy-policy" element={<ComingSoonPage title="Privacy Policy" 
            description="Our privacy policy details will be available here." />} />
          <Route path="legal-terms" element={<ComingSoonPage title="Legal Terms" 
            description="Legal terms and conditions will be available here." />} />
          
          {/* Legacy Redirect */}
          <Route path="create-treatment-plan/:patientId" element={<CreateTreatmentPlan />} />
          <Route path="create-treatment-plan/new" element={<RedirectToTreatmentPlans />} />
        </Route>

        {/* Patient Routes */}
        <Route path="/patient-area" element={<PatientPageLayout />}>
          <Route path="messages/:doctorId" element={<PatientChatPage />} />
          <Route path="notifications" element={<Notifications />} />
        </Route>

        {/* Redirect legacy doctor-area paths to root paths */}
        <Route path="/doctor-area/*" element={<Navigate to="/" replace />} />
      </Routes>
    </AuthProvider>
  );
}

export default App;
